// useIsMobile — viewport-gate hook for the public-page mobile redesign.
//
// Uses matchMedia('(max-width: 760px)') — the same breakpoint as the
// existing mobile rules in src/styles/tokens.css (.lg-table-wrap, sidebar
// drawer mode, etc.) so the JS gate stays aligned with CSS.
//
// SSR-safe (returns false when window is undefined). Listens for `change`
// events on the MediaQueryList so resizing the browser hot-swaps between
// mobile and desktop component trees without a reload.
//
// State stays in the calling parent component so hot-swapping a screen's
// layout doesn't lose tab/filter/search state — see MobilePublicSchedule
// + MobileStandings + MobileResults wiring in src/screens/mobile/*.

import { useEffect, useState } from 'react';

export const MOBILE_BREAKPOINT_PX = 760;

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    // SSR / older test envs (JSDOM doesn't ship matchMedia by default)
    // — assume desktop. The component can re-evaluate on mount once the
    // env has the API.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
    setIsMobile(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // Newer Safari/Chrome support addEventListener; older Safari only has
    // the deprecated addListener. Probe and use whichever is available.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Legacy fallback.
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isMobile;
}
